import { safeJson } from './utils/fetch.mjs';

const RAINVIEWER_API = '/api/rainviewer/public/weather-maps.json';
const FRAME_COUNT = 6;
const TILE_ZOOM = 7;
const TILE_SIZE = 256;
const GRID_SIZE = 3;
const COLOR_SCHEME = 1; // Original — snow renders as blue/cyan
const TILE_OPTIONS = '1_1'; // smooth=1, snow=1
const MERCATOR_LAT_MAX = 85.05113;

// Standard Web Mercator lat/lon to tile x/y at given zoom
function latLonToTile(lat, lon, zoom) {
	const clampedLat = Math.max(-MERCATOR_LAT_MAX, Math.min(MERCATOR_LAT_MAX, lat));
	const n = 2 ** zoom;
	const x = Math.floor((lon + 180) / 360 * n);
	const latRad = clampedLat * Math.PI / 180;
	const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
	return { x, y };
}

// Fetch the list of available radar frames from RainViewer
async function getFrames() {
	const data = await safeJson(RAINVIEWER_API);
	if (!data?.radar?.past || !data.host) return null;
	const frames = data.radar.past.slice(-FRAME_COUNT);
	return { host: data.host, frames };
}

// Build a GRID_SIZE x GRID_SIZE grid of proxied tile URLs centered on lat/lon for one radar frame
function buildTileGrid(framePath, lat, lon) {
	const center = latLonToTile(lat, lon, TILE_ZOOM);
	const grid = [];
	const half = Math.floor(GRID_SIZE / 2);
	for (let dy = -half; dy <= half; dy += 1) {
		for (let dx = -half; dx <= half; dx += 1) {
			const tx = center.x + dx;
			const ty = center.y + dy;
			const url = `/api/rainviewer-tiles${framePath}/${TILE_SIZE}/${TILE_ZOOM}/${tx}/${ty}/${COLOR_SCHEME}/${TILE_OPTIONS}.png`;
			grid.push({ url, tx, ty });
		}
	}
	const gridOrigin = { x: center.x - half, y: center.y - half };
	return { grid, gridOrigin, center };
}

// Convert a canvas (regular or OffscreenCanvas) to a data: URL.
// OffscreenCanvas lacks toDataURL(), so we go via blob → FileReader.
// A data: URL is required (not a blob: URL) because drawCanvas measures
// rendered image height synchronously to position radar frames.
async function canvasToDataURL(canvas) {
	if (canvas instanceof OffscreenCanvas) {
		const blob = await canvas.convertToBlob({ type: 'image/png' });
		return new Promise((resolve) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result);
			reader.readAsDataURL(blob);
		});
	}
	return canvas.toDataURL();
}

// Stitch a GRID_SIZE x GRID_SIZE grid of transparent radar tiles onto a canvas, return dataURL
// Returns null if no tiles loaded successfully.
async function stitchTiles(tileGrid) {
	const canvasSize = GRID_SIZE * TILE_SIZE;
	let canvas;
	if (typeof OffscreenCanvas !== 'undefined') {
		canvas = new OffscreenCanvas(canvasSize, canvasSize);
	} else {
		canvas = document.createElement('canvas');
		canvas.width = canvasSize;
		canvas.height = canvasSize;
	}
	const ctx = canvas.getContext('2d');

	let loadedCount = 0;
	await Promise.all(tileGrid.grid.map(({ url, tx, ty }) => new Promise((resolve) => {
		const img = new Image();
		img.onload = () => {
			loadedCount += 1;
			const px = (tx - tileGrid.gridOrigin.x) * TILE_SIZE;
			const py = (ty - tileGrid.gridOrigin.y) * TILE_SIZE;
			ctx.drawImage(img, px, py);
			resolve();
		};
		img.onerror = () => resolve(); // tile unavailable — leave transparent
		img.src = url;
	})));

	if (loadedCount === 0) return null;
	return canvasToDataURL(canvas);
}

export { getFrames, buildTileGrid, stitchTiles };
